import { useState } from 'react';
import { Loader2 } from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useToast } from '@/components/ui/use-toast';
import { UserAutocomplete } from '@/components/ui/user-autocomplete';
import { usePartnerTypes } from '@/api/partner-types';
import { useCreatePartnerAccount } from '@/api/partner-accounts';
import type { PartnerType, OwnerInfo } from '@/types/api';

interface AddPartnerAccountDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddPartnerAccountDialog({
  open,
  onOpenChange,
}: AddPartnerAccountDialogProps): JSX.Element {
  const { toast } = useToast();
  const { data: partnerTypes, isLoading: isLoadingTypes } = usePartnerTypes();
  const createAccount = useCreatePartnerAccount();

  const [selectedTypeId, setSelectedTypeId] = useState<string>('');
  const [name, setName] = useState('');
  const [credentials, setCredentials] = useState<Record<string, string>>({});
  const [defaultOwner, setDefaultOwner] = useState<OwnerInfo | null>(null);

  const selectedType = partnerTypes?.find((pt) => pt.id === selectedTypeId);

  const handleTypeChange = (typeId: string) => {
    setSelectedTypeId(typeId);
    setCredentials({});
  };

  const handleCredentialChange = (key: string, value: string) => {
    setCredentials((prev) => ({ ...prev, [key]: value }));
  };

  const handleSubmit = async (e: React.SyntheticEvent) => {
    e.preventDefault();

    if (!selectedTypeId || !name || !defaultOwner) {
      toast({
        title: 'Validation Error',
        description: 'Please fill in all required fields including the default owner.',
        variant: 'destructive',
      });
      return;
    }

    try {
      await createAccount.mutateAsync({
        partnerTypeId: selectedTypeId,
        name,
        credentials,
        defaultOwnerEmail: defaultOwner.email,
      });

      toast({
        title: 'Success',
        description: 'Partner account connected successfully.',
      });

      // Reset form and close dialog
      setSelectedTypeId('');
      setName('');
      setCredentials({});
      setDefaultOwner(null);
      onOpenChange(false);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to connect partner account';
      toast({
        title: 'Error',
        description: message,
        variant: 'destructive',
      });
    }
  };

  const renderCredentialFields = (type: PartnerType) => {
    return type.authFields.map((field) => (
      <div key={field.key} className="space-y-2">
        <Label htmlFor={`cred-${field.key}`}>
          {field.label}
          {field.required && <span className="text-destructive"> *</span>}
        </Label>
        <Input
          id={`cred-${field.key}`}
          type={field.type}
          placeholder={field.placeholder}
          value={credentials[field.key] ?? ''}
          onChange={(e) => { handleCredentialChange(field.key, e.target.value); }}
          required={field.required}
          data-testid={`credential-${field.key}-input`}
        />
      </div>
    ));
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl">
        <form onSubmit={handleSubmit}>
          <DialogHeader>
            <DialogTitle>Connect Partner Account</DialogTitle>
            <DialogDescription>
              Connect your property management platform to sync listings automatically.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="partner-type">Platform *</Label>
              <Select
                value={selectedTypeId}
                onValueChange={handleTypeChange}
                disabled={isLoadingTypes}
              >
                <SelectTrigger id="partner-type" data-testid="partner-type-select">
                  <SelectValue placeholder="Select a platform" />
                </SelectTrigger>
                <SelectContent>
                  {partnerTypes?.map((type) => (
                    <SelectItem key={type.id} value={type.id}>
                      {type.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="account-name">Account Name *</Label>
              <Input
                id="account-name"
                placeholder="My Property Account"
                value={name}
                onChange={(e) => { setName(e.target.value); }}
                required
                data-testid="account-name-input"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="default-owner">Default Owner *</Label>
              <p className="text-xs text-muted-foreground mb-1">
                Locations published from this account will be owned by this user in ScoutAround.
              </p>
              <UserAutocomplete
                value={defaultOwner}
                onChange={setDefaultOwner}
                placeholder="Search user by email..."
                required
                data-testid="default-owner"
              />
            </div>

            {selectedType && renderCredentialFields(selectedType)}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => { onOpenChange(false); }}
              disabled={createAccount.isPending}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={createAccount.isPending || !selectedTypeId || !name || !defaultOwner}
              data-testid="connect-account-btn"
            >
              {createAccount.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
              Connect Account
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
